import { observer } from 'mobx-react';
import * as React from 'react';
import { Redirect, Route, RouteComponentProps, HashRouter as Router, Switch } from 'react-router-dom';
import { AppState } from '../../../models/AppState';
import { useAdminUserSession } from '../../../models/hooks/adminStateHooks';
import { AdminPermission } from '../../../viewmodels/AdminViewModels';
import { ErrorMessagesProvider } from '../../../web/components/errorMessages/ErrorMessagesProvider';
import { RouteWithLoggedPageView } from '../../../web/components/PrivateRoute';
import { ScrollRestoration } from '../../../web/components/ScrollRestoration';
import { Login } from '../../../web/containers/Login';
import { Logout } from '../../../web/containers/Logout';
import { OAuthCallback } from '../../../web/containers/OAuthCallback';
import { AutomationFlowRedirect } from '../../../web/pages/AutomationFlowEditor';
import { AdminPrivateRoute } from '../../components/AdminPrivateRoute';
import { AdminFullscreenModal } from '../../components/fullscreen/AdminFullscreenModal';
import { AdminAccountBlogEditorPage } from '../../pages/AdminAccountBlogEditorPage';
import { AdminAccountBlogImportHistoryPage } from '../../pages/AdminAccountBlogImportHistoryPage';
import { AdminAccountBlogSettingsPage } from '../../pages/AdminAccountBlogSettingsPage';
import { AdminAccountBlogTemplateDesignerPage } from '../../pages/AdminAccountBlogTemplateDesignerPage';
import { AdminAccountConfigureIntegrationPage } from '../../pages/AdminAccountConfigureIntegrationPage';
import { AdminAccountConfirmationPage } from '../../pages/AdminAccountConfirmationPage';
import { AdminAccountContentCalendarPage } from '../../pages/AdminAccountContentCalendarPage';
import { AdminAccountCreateChargifyPage } from '../../pages/AdminAccountCreateChargifyPage';
import { AdminAccountCustomerAssistantPage } from '../../pages/AdminAccountCustomerAssistantPage';
import { AdminAccountDataRefreshPage } from '../../pages/AdminAccountDataRefreshPage';
import { AdminAccountDetailsPage } from '../../pages/AdminAccountDetailsV2Page';
import { AdminAccountIntegrationsPage } from '../../pages/AdminAccountIntegrationsPage';
import { AdminAccountNewAutomationPage } from '../../pages/AdminAccountNewAutomationPage';
import { AdminAccountSendOnBehalfPermissionsPage } from '../../pages/AdminAccountSendOnBehalfPermissionsPage';
import { AdminAccountsPage } from '../../pages/AdminAccountsPage';
import { AdminAdsMetaPage } from '../../pages/AdminAdsMetaPage';
import { AdminAgentTemplatesPage } from '../../pages/AdminAgentTemplatesPage';
import { AdminAutomationCanvasPage } from '../../pages/AdminAutomationCanvasPage';
import { AdminAutomationsPage } from '../../pages/AdminAutomationsPage';
import { AdminBillingOpenInvoicesPage } from '../../pages/AdminBillingOpenInvoicesPage';
import { AdminCampaignDebugPage } from '../../pages/AdminCampaignDebugPage';
import { AdminCampaignsGhostCalendarPage } from '../../pages/AdminCampaignsGhostCalendarPage';
import { AdminCampaignsPage } from '../../pages/AdminCampaignsPage';
import { AdminCollectPaymentConfirmationPage } from '../../pages/AdminCollectPaymentConfirmationPage';
import { AdminCollectPaymentPage } from '../../pages/AdminCollectPaymentPage';
import { AdminCreateAccountPage } from '../../pages/AdminCreateAccountPage';
import { AdminCreateSurveyTemplatePage } from '../../pages/AdminCreateSurveyTemplatePage';
import { AdminDevToolsPage } from '../../pages/AdminDevToolsPage';
import { AdminEditSurveyTemplatePage } from '../../pages/AdminEditSurveyTemplatePage';
import { AdminIndustryAutomationCanvasPage } from '../../pages/AdminIndustryAutomationCanvasPage';
import { AdminIndustryNewAutomationPage } from '../../pages/AdminIndustryNewAutomationPage';
import { AdminLogMeetingNotesPage } from '../../pages/AdminLogMeetingNotesPage';
import { AdminNotFoundPage } from '../../pages/AdminNotFoundPage';
import { AdminPricingWizardPage } from '../../pages/AdminPricingWizardPage';
import { AdminReportsDailyPage } from '../../pages/AdminReportsDailyPage';
import { AdminReportsMonthlyPage } from '../../pages/AdminReportsMonthlyPage';
import { AdminReportsPage } from '../../pages/AdminReportsPage';
import { AdminSurveyLibraryPage } from '../../pages/AdminSurveyLibraryPage';
import { AdminToolsPage } from '../../pages/AdminToolsPage';
import { AdminUpgradeWizardPage } from '../../pages/AdminUpgradeWizardPage';
import { AdminAPIKeysPage } from '../account/apiKeys/AdminAPIKeysPage';
import { AdminSupportInboxPage } from '../../pages/AdminSupportInboxPage';
import { AdminSupportSettingsPage } from '../../pages/AdminSupportSettingsPage';
import { LoadingSpinner } from '@WebComponents/LoadingSpinner';
import { AdminDashboardPageLayout } from '../AdminDashboardPageLayout';

function _AdminRouter() {
	const userSession = useAdminUserSession();
	React.useEffect(() => {
		if (window.location.href.includes('secure.levitate.ai/admin.html')) {
			window.location.replace('https://admin.levitate.ai/' + window.location.hash);
		}
	}, []);
	const hasManageTemplatesPermission = userSession?.hasPermission(AdminPermission.ManageTemplates);
	const hasManageSurveyTemplatesPermission = userSession?.hasPermission(AdminPermission.ManageSurveyTemplates);
	const hasManageAIAgentTemplatesPermission = userSession?.hasPermission(AdminPermission.ManageAIAgentTemplates);
	const hasOnlyForDevelopersPermission = userSession?.hasPermission(AdminPermission.OnlyForDevelopers);
	// Developers retain support access as a debugging escape hatch.
	const hasSupportAccess = !!userSession?.canAccessSupport || hasOnlyForDevelopersPermission;
	const hasCanViewOpenInvoicesPermission =
		userSession?.hasPermission(AdminPermission.ViewOpenInvoices) ||
		userSession?.hasPermission(AdminPermission.OnlyForDevelopers);

	const onRenderLogin = (props: RouteComponentProps<any>) => {
		return <Login {...props} loginMode='admin' userSession={userSession} />;
	};
	return (
		<Router>
			<>
				{!!AppState.errorMessages && <ErrorMessagesProvider errorMessageViewModel={AppState.errorMessages} />}
				<Switch>
					<Route exact={true} path='/'>
						<Redirect to='/login' />
					</Route>
					<RouteWithLoggedPageView path='/login' pageViewLogName='login' render={onRenderLogin} />
					<RouteWithLoggedPageView path='/logout' pageViewLogName='logout'>
						<Logout userSession={userSession} />
					</RouteWithLoggedPageView>
					<RouteWithLoggedPageView path='/applinks/launch/auth/oauth-callback' pageViewLogName='admin/oauth-callback'>
						<OAuthCallback userSession={userSession} isAdmin={true} />
					</RouteWithLoggedPageView>
					<AdminPrivateRoute>
						<Switch>
							<AdminPrivateRoute
								exact={true}
								path='/accounts/:id/integration/:integrationType'
								userSession={userSession}
							>
								<AdminAccountConfigureIntegrationPage />
							</AdminPrivateRoute>
							<AdminPrivateRoute exact={true} path='/accounts/:id/automations/new' userSession={userSession}>
								<AdminAccountNewAutomationPage />
							</AdminPrivateRoute>
							<AdminPrivateRoute exact={true} path='/accounts/:accountId/automations/:id' userSession={userSession}>
								<AutomationFlowRedirect />
							</AdminPrivateRoute>
							<AdminPrivateRoute exact path='/accounts/:accountId/import/data-refresh' userSession={userSession}>
								<AdminAccountDataRefreshPage />
							</AdminPrivateRoute>
							<AdminPrivateRoute exact={true} path='/accounts' pageViewLogName='admin/dashboard'>
								<AdminAccountsPage />
							</AdminPrivateRoute>
							<AdminPrivateRoute
								exact={true}
								path='/accounts/:id/create-account-chargify'
								pageViewLogName='admin/create-account-chargify'
							>
								<AdminAccountCreateChargifyPage />
							</AdminPrivateRoute>
							<AdminPrivateRoute exact={true} path='/accounts/create-account' pageViewLogName='admin/create-account'>
								<AdminCreateAccountPage />
							</AdminPrivateRoute>
							<AdminPrivateRoute
								exact={true}
								path='/accounts/create-account-v2'
								pageViewLogName='admin/create-account-v2'
							>
								<AdminCreateAccountPage />
							</AdminPrivateRoute>
							<AdminPrivateRoute
								exact={true}
								path='/accounts/:id/integrations/:integrationProvider/:tab?'
								pageViewLogName='admin/account-integrations'
							>
								<AdminAccountIntegrationsPage />
							</AdminPrivateRoute>
							<AdminPrivateRoute
								exact={true}
								path='/accounts/:id/content-calendar'
								pageViewLogName='admin/account-content-calendar'
							>
								<AdminAccountContentCalendarPage />
							</AdminPrivateRoute>
							<AdminPrivateRoute exact={true} path='/accounts/:id/blog-settings' pageViewLogName='admin/blog-settings'>
								<AdminAccountBlogSettingsPage />
							</AdminPrivateRoute>
							<AdminPrivateRoute
								exact={true}
								path='/accounts/:id/blog-import-history'
								pageViewLogName='admin/blog-import-history'
							>
								<AdminAccountBlogImportHistoryPage />
							</AdminPrivateRoute>
							<AdminPrivateRoute exact={true} path='/accounts/:id/blog-editor' pageViewLogName='admin/blog-editor'>
								<AdminAccountBlogEditorPage />
							</AdminPrivateRoute>
							<AdminPrivateRoute
								exact={true}
								path='/accounts/:id/template-designer'
								pageViewLogName='admin/template-designer'
							>
								<AdminAccountBlogTemplateDesignerPage />
							</AdminPrivateRoute>
							<AdminPrivateRoute
								exact={true}
								path='/accounts/:id/confirmation'
								pageViewLogName='admin/account-confirmation'
							>
								<AdminAccountConfirmationPage />
							</AdminPrivateRoute>
							<AdminPrivateRoute
								exact={true}
								path='/accounts/:accountId/api-keys'
								pageViewLogName='admin/api-keys'
								userSession={userSession}
							>
								<AdminAPIKeysPage />
							</AdminPrivateRoute>
							<AdminPrivateRoute
								exact={true}
								path='/accounts/:accountId/automations/:id/canvas'
								pageViewLogName='admin/automation-canvas'
							>
								<AdminAutomationCanvasPage />
							</AdminPrivateRoute>

							<AdminPrivateRoute
								exact={true}
								path='/accounts/:accountId/customer-assistant'
								pageViewLogName='admin/customer-assistant'
							>
								<AdminAccountCustomerAssistantPage />
							</AdminPrivateRoute>
							<AdminPrivateRoute
								exact={true}
								path='/accounts/:accountId/send-on-behalf-permissions'
								pageViewLogName='admin/account-send-on-behalf-permissions'
							>
								<AdminAccountSendOnBehalfPermissionsPage />
							</AdminPrivateRoute>

							<AdminPrivateRoute path='/accounts/:accountId/:tab?' pageViewLogName='admin/account-detail'>
								<React.Suspense
									fallback={
										<AdminDashboardPageLayout>
											<LoadingSpinner />
										</AdminDashboardPageLayout>
									}
								>
									<AdminAccountDetailsPage />
								</React.Suspense>
							</AdminPrivateRoute>
							{hasOnlyForDevelopersPermission ? (
								<AdminPrivateRoute path='/devtools' pageViewLogName='admin/devtools'>
									<AdminDevToolsPage />
								</AdminPrivateRoute>
							) : null}
							<AdminPrivateRoute path='/tools/pricing-wizard/:accountId?' pageViewLogName='admin/tools/pricing-wizard'>
								<AdminPricingWizardPage />
							</AdminPrivateRoute>
							<AdminPrivateRoute path='/tools/upgrade-wizard/:accountId' pageViewLogName='admin/tools/upgrade-wizard'>
								<AdminUpgradeWizardPage />
							</AdminPrivateRoute>
							<AdminPrivateRoute
								exact={true}
								path='/tools/collect-payment/confirmation'
								pageViewLogName='admin/tools/collect-payment-confirmation'
							>
								<AdminCollectPaymentConfirmationPage />
							</AdminPrivateRoute>
							<AdminPrivateRoute
								path='/tools/collect-payment/:pricebookItemId/:accountId?'
								pageViewLogName='admin/tools/collect-payment'
							>
								<AdminCollectPaymentPage />
							</AdminPrivateRoute>
							<AdminPrivateRoute
								exact={true}
								path='/tools/log-meeting-notes'
								pageViewLogName='admin/tools/log-meeting-notes'
							>
								<AdminLogMeetingNotesPage />
							</AdminPrivateRoute>
							<AdminPrivateRoute path='/tools' pageViewLogName='admin/tools'>
								<AdminToolsPage />
							</AdminPrivateRoute>
							<AdminPrivateRoute
								exact={true}
								path='/ads/meta'
								pageViewLogName='admin/ads/meta'
								requiredPermissions={AdminPermission.ManageAdsIntegrations}
							>
								<AdminAdsMetaPage />
							</AdminPrivateRoute>
							{hasCanViewOpenInvoicesPermission ? (
								<AdminPrivateRoute
									path='/billing/openInvoices'
									exact={true}
									pageViewLogName='admin/billing/openInvoices'
								>
									<AdminBillingOpenInvoicesPage />
								</AdminPrivateRoute>
							) : null}
							<AdminPrivateRoute path='/reports/daily' pageViewLogName='admin/reports/daily'>
								<AdminReportsDailyPage />
							</AdminPrivateRoute>
							<AdminPrivateRoute path='/reports/monthly' pageViewLogName='admin/reports/monthly'>
								<AdminReportsMonthlyPage />
							</AdminPrivateRoute>
							<AdminPrivateRoute path='/reports' pageViewLogName='admin/reports'>
								<AdminReportsPage />
							</AdminPrivateRoute>
							{hasManageTemplatesPermission ? (
								<AdminPrivateRoute path='/campaigns/ghost-calendar' exact={true} pageViewLogName='admin/ghost-calendar'>
									<AdminCampaignsGhostCalendarPage />
								</AdminPrivateRoute>
							) : null}
							{hasManageTemplatesPermission ? (
								<AdminPrivateRoute
									path='/campaigns/:campaignId/debug'
									exact={true}
									pageViewLogName='admin/campaign-debug'
								>
									<AdminCampaignDebugPage />
								</AdminPrivateRoute>
							) : null}
							{hasManageTemplatesPermission ? (
								<AdminPrivateRoute path='/campaigns' pageViewLogName='admin/campaigns'>
									<AdminCampaignsPage />
								</AdminPrivateRoute>
							) : null}
							{hasManageTemplatesPermission ? (
								<AdminPrivateRoute exact={true} path='/automations/new' pageViewLogName='admin/automations/new'>
									<AdminIndustryNewAutomationPage />
								</AdminPrivateRoute>
							) : null}
							{hasManageTemplatesPermission ? (
								<AdminPrivateRoute
									exact={true}
									path='/automations/:id/canvas'
									pageViewLogName='admin/automations/canvas'
								>
									<AdminIndustryAutomationCanvasPage />
								</AdminPrivateRoute>
							) : null}
							{hasManageTemplatesPermission ? (
								<AdminPrivateRoute path='/automations' pageViewLogName='admin/automations'>
									<AdminAutomationsPage />
								</AdminPrivateRoute>
							) : null}
							{hasManageSurveyTemplatesPermission ? (
								<AdminPrivateRoute
									exact={true}
									path='/surveys/template/custom/new'
									pageViewLogName='admin/surveys/template/custom/new'
								>
									<AdminCreateSurveyTemplatePage />
								</AdminPrivateRoute>
							) : null}
							{hasManageSurveyTemplatesPermission ? (
								<AdminPrivateRoute
									exact={true}
									path='/surveys/template/custom/:id/edit'
									pageViewLogName='admin/surveys/template/custom/edit'
								>
									<AdminEditSurveyTemplatePage />
								</AdminPrivateRoute>
							) : null}
							{hasManageSurveyTemplatesPermission ? (
								<AdminPrivateRoute path='/surveys' pageViewLogName='admin/surveys'>
									<AdminSurveyLibraryPage />
								</AdminPrivateRoute>
							) : null}
							{hasManageAIAgentTemplatesPermission ? (
								<AdminPrivateRoute path='/ai-agent-templates' pageViewLogName='admin/ai-agent-templates'>
									<AdminAgentTemplatesPage />
								</AdminPrivateRoute>
							) : null}
							{hasSupportAccess ? (
								<AdminPrivateRoute
									path='/support/settings/:mailboxId?/:section?'
									pageViewLogName='admin/support/settings'
								>
									<AdminSupportSettingsPage />
								</AdminPrivateRoute>
							) : null}
							{hasSupportAccess ? (
								<AdminPrivateRoute path='/support' pageViewLogName='admin/support'>
									<AdminSupportInboxPage />
								</AdminPrivateRoute>
							) : null}
							{/* This must be last.*/}
							<AdminPrivateRoute pageViewLogName='admin/not-found'>
								<AdminNotFoundPage />
							</AdminPrivateRoute>
						</Switch>
					</AdminPrivateRoute>
				</Switch>
				<AdminFullscreenModal />
			</>
			<ScrollRestoration />
		</Router>
	);
}

export const AdminRouter = observer(_AdminRouter);
